/**
 * A moldura do cockpit: aviso, barra lateral, topo de contexto, conteúdo e selo.
 *
 * **A navegação saiu do topo e virou lateral em 15/09**, e isso é mudança do design system
 * (§3), não de CSS. A regra antiga — "as abas ficam ao lado da marca" — nasceu quando havia
 * **três** abas e nenhuma delas tinha sub-tela. Com a administração, são quatro áreas, uma
 * delas com cinco seções dentro, e a barra do topo passou a disputar espaço com o que ela
 * existe para carregar: a concessionária, o cenário da demonstração, o relógio e o estado
 * do sistema. Numa barra cheia, "trocar de módulo" some no meio de "mudar de contexto" —
 * que são coisas diferentes e precisam parecer diferentes.
 *
 * A lateral também resolve o que a horizontal não resolvia: **cabe rótulo inteiro, cabe
 * sub-item e cabe dizer o que cada área faz**. É por isso que a ajuda mora aqui e não numa
 * página separada — ajuda que exige sair da tela para ser lida não é lida.
 *
 * **A página não rola quando cabe.** A rolagem vive dentro do corpo de cada painel. Numa sala
 * de monitoramento, rolar a página tira a régua de KPIs de vista, e a régua existe justamente
 * para ser lida de três metros sem ninguém mexer em nada. **Quando não cabe, rola** (25/09):
 * num notebook de 1366 × 768 a SENATRAN deixava a tabela e o detalhe com a altura de uma
 * linha, cortados pelo `overflow-hidden` — a Gisele não via o detalhe do envio. A área de
 * trabalho de cada tela tem uma altura mínima (`md:min-h-[360px]` na SENATRAN), e abaixo dela
 * a página rola em vez de esconder.
 *
 * **Abaixo de 768 px, nada disso vale — e isso é a `C-17`.** A `C-01` nasceu para 1280×720:
 * num celular de 390 px a lateral ficava com 60% da tela, o conteúdo sobrava em 158 px, era
 * cortado pelo `overflow-hidden` e a página não rolava. Parecia que não tinha carregado. No
 * celular a navegação é uma **barra inferior** com ícone e rótulo curto, o topo compacta, a
 * ajuda e o "sair" vão para o topo, e **a página rola** — a regra da sala de monitoramento é
 * da sala de monitoramento. A partir de `md` a moldura é exatamente a que era: os utilitários
 * `md:` abaixo são a linha que separa os dois mundos, e nenhuma tela precisou saber disso.
 *
 * **O topo do celular é uma linha só** (06/10, `C-17` fase 2). Eram quatro — a faixa de
 * prévia, a marca com a concessionária, o cenário da demonstração, o ambiente com a saúde e
 * os botões —, perto de um terço de um iPhone antes do primeiro número. Agora: a marca, o
 * *Pergunte* (nas telas de gestão) e um menu, que guarda o resto — concessionária, pessoa,
 * ambiente e saúde, o cenário da demonstração, a ajuda, o sair e a assinatura de quem
 * entrega. O topo fica preso em cima enquanto a página rola, e o ponto vermelho no botão do
 * menu acende quando o sistema cai: o aviso que importa não fica escondido.
 */
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router";

import { avisoDeValidadeVisivel } from "../acesso";
import { useSessao } from "../api/contexto-sessao";
import { avisoDaCota } from "../cotas";
import { dataEHora, numeroLongo } from "../formato";
import { useAlertasDoMenu } from "../ganchos/useAlertasDoMenu";
import { useModulos } from "../ganchos/useModulos";
import { useNavegacao } from "../ganchos/useNavegacao";
import { useAmbiente } from "../ganchos/useAmbiente";
import {
  ContextoDaContingencia,
  useContingencia,
} from "../ganchos/useContingencia";
import { useFocoDeVolta } from "../ganchos/useFocoDeVolta";
import { useRelogioDaOrganizacao } from "../ganchos/useRelogioDaOrganizacao";
import { useSaude } from "../ganchos/useSaude";
import { useTetosDoContrato } from "../ganchos/useTetosDoContrato";
import { agoraDoInquilino } from "../relogio";
import { AvisoDeValidade } from "./AvisoDeValidade";
import { AvisoDeCota } from "./AvisoDeCota";
import { AvisoDeVersao } from "./AvisoDeVersao";
import { FaixaDaContingencia } from "./FaixaDaContingencia";
import { faixaVisivel } from "./contingencia";
import { RelogioDaDemonstracao } from "./RelogioDaDemonstracao";
import { SeletorDeCenario } from "./SeletorDeCenario";
import { Badge, Pilula } from "./base";
import { Icone } from "./icones";
import {
  AssinaturaDoFornecedor,
  FORNECEDOR,
  MarcaDoProduto,
  PRODUTO,
} from "./marca";
import {
  agenteNaTela,
  areaDe,
  areasDaNavegacao,
  areasVisiveis,
  controlaADemonstracao,
  eRegulador,
} from "./navegacao";
import { FaixaDoRegulador } from "./FaixaDoRegulador";
import { MarcaDagua } from "./MarcaDagua";
import { PergunteAoCockpit } from "./PergunteAoCockpit";
import { SeletorDeTema } from "./SeletorDeTema";
import type { Area } from "./navegacao";

const ICONE: Record<Area["icone"], () => React.JSX.Element> = {
  imagens: Icone.Foto,
  operacao: Icone.Lista,
  senatran: Icone.Enviar,
  prazos: Icone.Relogio,
  indicadores: Icone.Grafico,
  regulador: Icone.Escudo,
  autuacao: Icone.Nota,
  admin: Icone.Engrenagem,
};

/**
 * As linhas da mesa: cada tela desenha título, cartões e o painel que ocupa o resto (`1fr`).
 * Faixa por cima da tela — a ajuda aberta, o aviso da validade (`A-09`) — é uma linha a
 * mais antes delas, e o `1fr` desce junto; senão o painel cairia numa linha `auto` e a tela
 * inteira rolaria. Escritas por extenso para o Tailwind as achar.
 */
const LINHAS_DA_MESA = [
  "md:grid-rows-[auto_auto_1fr]",
  "md:grid-rows-[auto_auto_auto_1fr]",
  "md:grid-rows-[auto_auto_auto_auto_1fr]",
  "md:grid-rows-[auto_auto_auto_auto_auto_1fr]",
  "md:grid-rows-[auto_auto_auto_auto_auto_auto_1fr]",
];

export function Moldura() {
  const { quem, sair } = useSessao();
  const saude = useSaude();
  const ambiente = useAmbiente();
  // O relógio da organização (ADR-059): lido aqui, uma vez, para todas as telas contarem os
  // prazos no tempo dela. Só em demonstração.
  const demonstracao = useRelogioDaOrganizacao(!!quem?.modo_demonstracao);
  const { licenciados } = useModulos();
  const { pathname } = useLocation();
  const [ajudaAberta, definirAjudaAberta] = useState(false);
  // O aviso da validade (`A-09`) dura uma semana: calculado aqui para as linhas da tela
  // descerem uma, em vez de a faixa empurrar o painel da tela para fora do `1fr`.
  const validadeNaTela = avisoDeValidadeVisivel(quem?.acesso_ate, quem?.perfil);
  // A faixa das cotas do contrato (`B-03`): também só do administrador, pelo mesmo motivo.
  const avisoDasCotas = avisoDaCota(
    useTetosDoContrato(quem?.perfil === "ADMIN"),
  );
  const [menuAberto, definirMenuAberto] = useState(false);
  // Trocou de tela: o menu do celular fecha — ele é de passagem, não um lugar.
  const [telaDoMenu, definirTelaDoMenu] = useState(pathname);
  if (telaDoMenu !== pathname) {
    definirTelaDoMenu(pathname);
    if (menuAberto) definirMenuAberto(false);
  }
  // O agente do Cockpit é o módulo WhatsBI Cockpit (ADR-043): sem ele licenciado, o botão
  // não aparece. As perguntas que ele responde vêm dos outros módulos licenciados. Em toda
  // área (03/11): antes só nas de gestão, e a Gisele tinha de abrir a Administração para
  // achá-lo — `agenteNaTela`, em `navegacao.ts`, só o tira do regulador.
  const [agenteAberto, definirAgenteAberto] = useState(false);
  const agenteAqui =
    licenciados.includes("whatsbi") && agenteNaTela(pathname, quem?.perfil);
  // O poder concedente só lê (`OB-13`): a faixa diz isso o tempo todo.
  const regulador = eRegulador(quem?.perfil);
  // Saiu para uma tela sem o agente: o painel fecha, e não reabre sozinho na volta.
  if (agenteAberto && !agenteAqui) definirAgenteAberto(false);
  // O foco volta a quem abriu o painel, a ajuda e o menu quando eles fecham (`N254`).
  const focoDoAgente = useFocoDeVolta();
  const focoDaAjuda = useFocoDeVolta();
  const focoDoMenu = useFocoDeVolta();
  const alternarAgente = () => {
    if (agenteAberto) focoDoAgente.devolver();
    else focoDoAgente.lembrar();
    definirAgenteAberto((a) => !a);
  };
  const fecharAgente = () => {
    focoDoAgente.devolver();
    definirAgenteAberto(false);
  };
  const alternarAjuda = () => {
    if (ajudaAberta) focoDaAjuda.devolver();
    else focoDaAjuda.lembrar();
    definirAjudaAberta((a) => !a);
  };
  const fecharAjuda = () => {
    focoDaAjuda.devolver();
    definirAjudaAberta(false);
  };
  const alternarMenu = () => {
    if (menuAberto) focoDoMenu.devolver();
    else focoDoMenu.lembrar();
    definirMenuAberto((a) => !a);
  };
  const fecharMenu = () => {
    focoDoMenu.devolver();
    definirMenuAberto(false);
  };
  // O menu é do servidor (`T-21`, ADR-085); sem a resposta dele, a conta de licença e perfil.
  const { codigos } = useNavegacao();
  const areas = codigos
    ? areasDaNavegacao(codigos)
    : areasVisiveis(quem?.perfil, licenciados);
  const atual = areaDe(pathname);
  // A SENATRAN fora do ar (`R-06`): a faixa é de quem opera. O regulador não opera a fila, e
  // a rota não é aberta a ele.
  const contingencia = useContingencia(
    licenciados.includes("tollvision") && !regulador,
    pathname,
  );
  const faixas =
    (validadeNaTela ? 1 : 0) +
    (avisoDasCotas ? 1 : 0) +
    (regulador ? 1 : 0) +
    (faixaVisivel(contingencia.contingencia) ? 1 : 0) +
    (ajudaAberta && atual ? 1 : 0);
  // O prazo de 24 h à vista também no menu (05/10): quem está em Indicadores vê que a
  // Operação tem trânsito vencendo, sem abrir a Operação. O regulador não opera a fila, e a
  // contagem relida a cada minuto seria leitura dele na trilha sem ele ter aberto nada.
  const demonstracaoNoAr = !!quem?.modo_demonstracao;
  const alertas = useAlertasDoMenu(
    licenciados.includes("tollvision") && !regulador,
    pathname,
  );

  return (
    <div
      className={`grid min-h-full md:h-full md:overflow-hidden ${
        demonstracaoNoAr ? "grid-rows-[auto_1fr]" : "grid-rows-[1fr]"
      }`}
    >
      {/* A faixa diz que os dados são sintéticos (`N256`): só onde isso é verdade. Numa organização
          de verdade ela diria uma coisa falsa na cara do cliente. */}
      {demonstracaoNoAr && <Aviso />}
      {/* A marca d'água (`A-09`): por cima de tudo, só onde o dono do banco ligou. */}
      {quem?.marca_dagua && <MarcaDagua email={quem.email} />}

      <div className="grid min-h-0 min-w-0 md:grid-cols-[auto_1fr]">
        {/* A lateral é da mesa. No celular ela some inteira, e quem a substitui é a
            `BarraInferior`, lá embaixo — não uma lateral "colapsada" que rouba 60 px e
            ainda precisa de um botão-hambúrguer para dizer onde se está. */}
        <nav
          aria-label="Áreas do cockpit"
          className="hidden min-h-0 w-[232px] grid-rows-[auto_1fr_auto] border-r border-linha bg-topo md:grid"
        >
          <div className="border-b border-linha px-3 py-2.5">
            <MarcaDoProduto />
          </div>

          <div className="grid content-start gap-0.5 overflow-y-auto p-2">
            {areas.map((area) => {
              const Desenho = ICONE[area.icone];
              return (
                <NavLink
                  key={area.para}
                  to={area.para}
                  title={area.resumo}
                  className={({ isActive }) =>
                    `grid grid-cols-[auto_1fr] items-center gap-2 rounded-botao border-l-2 px-2.5 py-2 text-left transition-colors ${
                      isActive
                        ? "border-l-azul bg-realce text-tinta"
                        : "border-l-transparent text-tinta-3 hover:bg-painel-2 hover:text-tinta-2"
                    }`
                  }
                >
                  <Desenho />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-[12px] font-semibold">
                      {area.rotulo}
                      <AlertaDoMenu quantidade={alertas[area.para]} />
                    </span>
                    {/* O resumo é a mesma frase que vira subtítulo da tela: uma fonte só. */}
                    <small className="block truncate text-[10px] text-tinta-3">
                      {area.resumo}
                    </small>
                  </span>
                </NavLink>
              );
            })}

            {agenteAqui && (
              <ConviteDoAgente
                aberto={agenteAberto}
                aoAlternar={alternarAgente}
              />
            )}
          </div>

          {/* A ajuda mora no menu porque é ali que se procura quando não se sabe onde ir.
              Ajuda que exige sair da tela para ser lida não é lida. */}
          <div className="grid gap-2 border-t border-linha p-2">
            <button
              type="button"
              aria-expanded={ajudaAberta}
              onClick={alternarAjuda}
              className="flex items-center gap-2 rounded-botao border border-linha bg-painel-2 px-2.5 py-1.5 text-[11.5px] text-tinta-2 hover:bg-realce"
            >
              <Icone.Duvida />
              {ajudaAberta ? "Fechar a ajuda" : "O que faz esta tela?"}
            </button>
            {quem && (
              <div className="grid gap-1 px-1 pb-1">
                <span className="truncate text-[11.5px] text-tinta">
                  {quem.nome}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="rounded-pilula border border-linha bg-painel-2 px-2 py-0.5 text-[9.5px] tracking-[0.04em] text-tinta-3 uppercase">
                    {quem.perfil}
                  </span>
                  <button
                    type="button"
                    onClick={() => void sair()}
                    className="rounded-botao border border-linha bg-painel-2 px-2 py-0.5 text-[11px] text-tinta-2 hover:bg-realce"
                  >
                    Sair
                  </button>
                </span>
              </div>
            )}
            {/* O tema é da pessoa (`C-19`): mora com o nome dela, e não no topo da tela. */}
            <div className="px-1">
              <SeletorDeTema />
            </div>

            {/* Quem entrega, no pé da lateral. O topo da tela é da concessionária e a
                lateral é do produto — sobra este canto, que é onde uma assinatura
                pertence: presente o tempo todo, disputando atenção com nada. */}
            <div className="border-t border-linha px-1 pt-2">
              <AssinaturaDoFornecedor />
            </div>
          </div>
        </nav>

        <div className="grid min-h-0 min-w-0 grid-rows-[auto_1fr_auto]">
          <header className="sticky top-0 z-20 flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-linha bg-topo px-3 py-2 md:static">
            {/* No celular a marca precisa estar em algum lugar, e a lateral não existe. */}
            <span className="md:hidden">
              <MarcaDoProduto />
            </span>

            {/* O topo ficou com o que ele sempre foi: **contexto**, não navegação. No celular,
                o contexto mora no menu. */}
            {quem && (
              <span className="hidden min-w-0 flex-col leading-tight md:flex">
                <Concessionaria nome={quem.organizacao_nome} />
              </span>
            )}

            {/* Só em demonstração: numa instalação de produção não existe botão que escolha
                o que o órgão de trânsito vai responder, e ele estar visível já diz isso. E só
                para quem apresenta (`O-06`): quem testa a demonstração vê a SENATRAN
                responder, e não o seletor que muda a resposta de todo mundo. */}
            {quem?.modo_demonstracao && (
              <span className="hidden md:contents">
                {controlaADemonstracao(quem) && <SeletorDeCenario />}
                <RelogioDaDemonstracao
                  relogio={demonstracao.relogio}
                  perfil={quem.perfil}
                  aoAvancar={demonstracao.avancar}
                />
              </span>
            )}

            <span className="flex-1" />

            <RelogioDoTopo />
            <span className="hidden items-center gap-3 md:flex">
              {ambiente && <Pilula cor="amarelo">{ambiente}</Pilula>}
              <Pilula cor={saude.cor}>● {saude.rotulo}</Pilula>
            </span>

            {/* No celular: o Pergunte e o menu. Alvos de 40 px: dedo, não ponteiro. */}
            <span className="flex items-center gap-1.5 md:hidden">
              {agenteAqui && (
                <button
                  type="button"
                  aria-label="Pergunte ao Cockpit"
                  aria-expanded={agenteAberto}
                  onClick={alternarAgente}
                  className="flex h-10 items-center gap-1.5 rounded-botao border border-azul-cheio bg-azul-cheio px-3 text-[12.5px] font-bold text-white shadow-[0_4px_14px_-6px_var(--cor-azul)]"
                >
                  <Icone.Conversa />
                  Pergunte
                </button>
              )}
              <button
                type="button"
                aria-label={menuAberto ? "Fechar o menu" : "Menu"}
                aria-expanded={menuAberto}
                aria-controls="menu-do-celular"
                onClick={alternarMenu}
                className="relative grid h-10 w-10 place-items-center rounded-botao border border-linha bg-painel-2 text-tinta-2"
              >
                {menuAberto ? <Icone.Fechar /> : <Icone.Menu />}
                {saude.cor === "vermelho" && (
                  <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-vermelho">
                    <span className="sr-only">{saude.rotulo}</span>
                  </span>
                )}
              </button>
            </span>

            {menuAberto && (
              <MenuDoCelular
                id="menu-do-celular"
                ajudaAberta={ajudaAberta}
                aoAlternarAjuda={() => {
                  definirAjudaAberta((a) => !a);
                  definirMenuAberto(false);
                }}
                aoSair={() => void sair()}
                aoFechar={fecharMenu}
              >
                {quem && (
                  <span className="grid gap-1 leading-tight">
                    <Concessionaria nome={quem.organizacao_nome} />
                    <span className="flex items-center gap-1.5 text-[11.5px] text-tinta-3">
                      {quem.nome}
                      <span className="rounded-pilula border border-linha bg-painel-2 px-2 py-0.5 text-[9.5px] tracking-[0.04em] uppercase">
                        {quem.perfil}
                      </span>
                    </span>
                  </span>
                )}
                <span className="flex flex-wrap items-center gap-2">
                  {ambiente && <Pilula cor="amarelo">{ambiente}</Pilula>}
                  <Pilula cor={saude.cor}>● {saude.rotulo}</Pilula>
                </span>
                {controlaADemonstracao(quem) && <SeletorDeCenario />}
                {quem?.modo_demonstracao && (
                  <RelogioDaDemonstracao
                    relogio={demonstracao.relogio}
                    perfil={quem.perfil}
                    aoAvancar={demonstracao.avancar}
                  />
                )}
                <SeletorDeTema grande />
              </MenuDoCelular>
            )}
          </header>

          {/* No celular: linhas do tamanho do conteúdo e a página rola. Na mesa: as três
              linhas de sempre e nada passa da borda. O `pb-24` deixa o fim do conteúdo
              visível acima da barra inferior — sem ele o último cartão fica embaixo dela.

              `min-w-0` aqui, nos dois `div` acima e em cada filho (`*:min-w-0`): item de
              grid tem `min-width: auto`, e nessa cadeia um `1fr` lá no fundo resolve pelo
              conteúdo máximo — um título de cartão sem quebra puxava a coluna inteira do
              cockpit para 524 px numa tela de 390, e o celular abria com zoom para fora. Com
              o mínimo em zero a largura da tela é a que manda, e quem não cabe quebra linha. */}
          <main
            className={`grid min-h-0 min-w-0 auto-rows-max content-start gap-3 p-3 pb-24 *:min-w-0 ${LINHAS_DA_MESA[faixas] ?? LINHAS_DA_MESA[0]} md:auto-rows-auto md:overflow-x-hidden md:overflow-y-auto md:pb-3`}
          >
            <AvisoDeVersao />
            <AvisoDeValidade
              acessoAte={quem?.acesso_ate}
              perfil={quem?.perfil}
            />
            <AvisoDeCota aviso={avisoDasCotas} />
            <FaixaDaContingencia
              contingencia={contingencia.contingencia}
              aoTentar={contingencia.definir}
            />
            {regulador && <FaixaDoRegulador />}
            {ajudaAberta && atual && (
              <Ajuda
                area={atual}
                regulador={regulador}
                aoFechar={fecharAjuda}
              />
            )}
            <ContextoDaContingencia.Provider value={contingencia.contingencia}>
              <Outlet />
            </ContextoDaContingencia.Provider>
          </main>

          {/* §3: o selo é permanente e não se fecha. É controle de risco comercial (`O-02`),
              não aviso — e por isso ele não some quando o operador se acostuma com ele. */}
          {quem?.modo_demonstracao && <RodapeDeAmbiente />}

          <BarraInferior areas={areas} alertas={alertas} />
          {agenteAberto && agenteAqui && (
            <PergunteAoCockpit aoFechar={fecharAgente} />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * O convite do *Pergunte ao Cockpit* na lateral (06/10): um cartão, não um botão de pé de
 * menu. Era um botão de 28 px no canto de baixo — o lugar que menos se olha numa tela — e o
 * agente é a parte do cockpit que mais diz o que ele é. Fica logo abaixo das áreas, na
 * altura dos olhos de quem acabou de escolher onde ir, com o ícone num círculo cheio e uma
 * linha que diz o que perguntar. É o único elemento em azul cheio da lateral: a área ativa é
 * `--realce`, e nada mais disputa.
 */
function ConviteDoAgente({
  aberto,
  aoAlternar,
}: {
  aberto: boolean;
  aoAlternar: () => void;
}) {
  return (
    <button
      type="button"
      aria-label="Pergunte ao Cockpit"
      aria-describedby="convite-do-agente"
      aria-expanded={aberto}
      onClick={aoAlternar}
      className={`mt-3 grid grid-cols-[auto_1fr] items-center gap-2 rounded-cartao border bg-gradient-to-br px-2.5 py-3 text-left transition-colors ${
        aberto
          ? "border-azul from-azul-cheio/45 to-realce"
          : "border-azul-borda from-azul-cheio/25 to-realce shadow-[0_6px_20px_-10px_var(--cor-azul)] hover:border-azul hover:from-azul-cheio/40"
      }`}
    >
      <span className="grid h-8 w-8 place-items-center rounded-full bg-azul-cheio text-white shadow-[0_0_0_3px_var(--cor-azul-fundo)]">
        <Icone.Conversa />
      </span>
      <span className="min-w-0">
        <span className="block text-[13px] leading-tight font-bold whitespace-nowrap text-tinta">
          Pergunte ao Cockpit
        </span>
        <small
          id="convite-do-agente"
          className="mt-0.5 block text-[10.5px] leading-snug text-azul-claro"
        >
          Perdas, envios e arrecadação, em português
        </small>
      </span>
    </button>
  );
}

/**
 * O que a tela faz — e, na mesma altura, o que ela **não** faz.
 *
 * Os dois lados juntos, de propósito. Uma ajuda que só explica o que existe deixa a
 * demonstração prometer o que o produto não entrega, e a conversa comercial segue por um
 * caminho que a engenharia não consegue cumprir. Cada limite cita a história que o resolve,
 * para a pergunta "quando?" ter uma resposta em vez de uma promessa.
 */
function Ajuda({
  area,
  regulador = false,
  aoFechar,
}: {
  area: Area;
  regulador?: boolean;
  aoFechar: () => void;
}) {
  // Para o regulador, o que a tela mostra a ele — e não as ações e as lacunas do produto,
  // que são conversa da concessionária.
  if (regulador)
    return (
      <section
        aria-label={`Ajuda — ${area.rotulo}`}
        className="grid gap-2 rounded-cartao border border-azul-borda bg-azul-fundo px-3 py-2.5"
      >
        <div className="flex flex-wrap items-center gap-2">
          <Badge cor="azul">{area.rotulo}</Badge>
          <button
            type="button"
            onClick={aoFechar}
            className="ml-auto rounded-botao border border-linha bg-painel-2 px-2 py-0.5 text-[11px] text-tinta-2 hover:bg-realce"
          >
            Fechar
          </button>
        </div>
        <p className="max-w-prose text-[12px] text-tinta-2">
          {area.ajudaDoRegulador ?? area.resumo}
        </p>
      </section>
    );
  return (
    <section
      aria-label={`Ajuda — ${area.rotulo}`}
      className="grid gap-2 rounded-cartao border border-azul-borda bg-azul-fundo px-3 py-2.5"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge cor="azul">{area.rotulo}</Badge>
        <button
          type="button"
          onClick={aoFechar}
          className="ml-auto rounded-botao border border-linha bg-painel-2 px-2 py-0.5 text-[11px] text-tinta-2 hover:bg-realce"
        >
          Fechar
        </button>
      </div>
      <p className="max-w-prose text-[12px] text-tinta-2">{area.ajuda}</p>
      <div className="grid gap-1">
        <h3 className="text-[10.5px] font-bold tracking-[0.07em] text-tinta-3 uppercase">
          O que esta tela ainda não faz
        </h3>
        <ul className="grid gap-0.5">
          {area.limites.map((limite) => (
            <li
              key={limite}
              className="flex items-start gap-1.5 text-[11.5px] text-tinta-3"
            >
              <span className="mt-0.5 text-amarelo">
                <Icone.Seta />
              </span>
              {limite}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Aviso() {
  return (
    <div className="flex items-center gap-2 border-b border-selo-borda bg-gradient-to-r from-selo-de to-selo-ate px-3 py-1 text-[11px] md:py-1.5">
      <b className="text-amarelo">Prévia</b>
      {/* No celular, uma linha: o que importa é "sintético", e cabe. */}
      <span className="text-selo-tinta md:hidden">dados sintéticos</span>
      <span className="hidden text-selo-tinta md:inline">
        Cockpit em construção — os dados desta organização são sintéticos.
      </span>
    </div>
  );
}

function Concessionaria({ nome }: { nome: string }) {
  return (
    <>
      <span className="text-rotulo tracking-[0.06em] text-azul-claro uppercase">
        Concessionária
      </span>
      <span className="max-w-[80vw] truncate text-tinta-2 md:max-w-[220px]">
        {nome}
      </span>
    </>
  );
}

/**
 * O menu do celular (06/10): o que o topo da mesa mostra o tempo todo e o celular não tem
 * onde pôr. Abre embaixo do topo, que é preso — então ele também fica à vista, onde a página
 * estiver. Fecha no Esc, no "Fechar o menu", ao trocar de tela e ao escolher a ajuda.
 */
function MenuDoCelular({
  id,
  ajudaAberta,
  aoAlternarAjuda,
  aoSair,
  aoFechar,
  children,
}: {
  id: string;
  ajudaAberta: boolean;
  aoAlternarAjuda: () => void;
  aoSair: () => void;
  aoFechar: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === "Escape") aoFechar();
    }
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [aoFechar]);
  return (
    <section
      id={id}
      aria-label="Menu"
      className="grid basis-full gap-3 border-t border-linha pt-3 pb-1 md:hidden"
    >
      {children}
      <span className="flex flex-wrap gap-2">
        <button
          type="button"
          aria-expanded={ajudaAberta}
          onClick={aoAlternarAjuda}
          className="flex h-10 items-center gap-2 rounded-botao border border-linha bg-painel-2 px-3 text-[12px] text-tinta-2"
        >
          <Icone.Duvida />
          {ajudaAberta ? "Fechar a ajuda" : "O que faz esta tela?"}
        </button>
        <button
          type="button"
          onClick={aoSair}
          className="h-10 rounded-botao border border-linha bg-painel-2 px-3 text-[12px] text-tinta-2"
        >
          Sair
        </button>
      </span>
      <span className="border-t border-linha pt-2">
        <AssinaturaDoFornecedor />
      </span>
    </section>
  );
}

/**
 * A navegação do celular (`C-17`). Mesma fonte que a lateral — as `areas` da moldura — então o
 * operador não vê aqui uma área que a lateral esconderia, e vice-versa. `sticky` e não
 * `fixed`: fica no fluxo da página, o que faz o `pb-24` do `main` bastar para nada ficar
 * escondido atrás dela, e respeita a área segura do iPhone.
 */
function BarraInferior({
  areas,
  alertas,
}: {
  areas: Area[];
  alertas: Record<string, number>;
}) {
  return (
    <nav
      aria-label="Áreas do cockpit, no celular"
      className="sticky bottom-0 z-10 grid border-t border-linha bg-topo pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      <div className="grid auto-cols-fr grid-flow-col">
        {areas.map((area) => {
          const Desenho = ICONE[area.icone];
          return (
            <NavLink
              key={area.para}
              to={area.para}
              title={area.rotulo}
              className={({ isActive }) =>
                `grid min-h-14 place-items-center gap-0.5 border-t-2 px-1 py-1.5 text-[10.5px] font-semibold ${
                  isActive
                    ? "border-t-azul bg-realce text-tinta"
                    : "border-t-transparent text-tinta-3"
                }`
              }
            >
              <span className="relative">
                <Desenho />
                <span className="absolute -top-2 left-3.5">
                  <AlertaDoMenu quantidade={alertas[area.para]} />
                </span>
              </span>
              <span className="truncate">{area.rotuloCurto}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

/**
 * Quantos vencem as 24 h em menos de quatro horas nesta área — o mesmo número do cartão de
 * prazo crítico da tela. Some com zero: um "0" em cada item do menu seria ruído, e o menu
 * só fala quando há o que fazer.
 */
function AlertaDoMenu({ quantidade }: { quantidade: number | undefined }) {
  if (!quantidade) return null;
  const frase = "vencem as 24 h em menos de 4 horas";
  return (
    <span
      title={`${numeroLongo(quantidade)} ${frase}`}
      className="mono rounded-pilula border border-vermelho-borda bg-vermelho-fundo px-1.5 text-[10px] leading-4 font-bold text-vermelho"
    >
      {numeroLongo(quantidade)}
      <span className="sr-only"> {frase}</span>
    </span>
  );
}

function RodapeDeAmbiente() {
  return (
    <footer className="flex items-center border-t border-linha bg-topo px-3 py-1.5 text-[11px] text-tinta-3">
      <span className="hidden md:inline">
        {PRODUTO.nome} {PRODUTO.complemento} · {FORNECEDOR.nome}{" "}
        {FORNECEDOR.sufixo}
      </span>
      <span className="mx-auto inline-flex items-center gap-1.5 rounded-pilula border border-amarelo-borda bg-amarelo-fundo px-2.5 py-0.5 font-semibold text-amarelo uppercase">
        <Icone.Alerta />
        Ambiente demonstrativo
      </span>
      <span className="hidden md:inline">
        Dados simulados para homologação e demonstração
      </span>
    </footer>
  );
}

/**
 * O relógio do topo — o da organização, que na demonstração pode estar à frente (ADR-059).
 *
 * Uma folha (`N259`): o estado que bate por segundo mora aqui, e não na `Moldura`, que re-renderizava
 * a lateral, o topo e o painel do agente aberto a cada segundo — com o relógio escondido
 * abaixo de `xl`. Em Brasília, como o resto da tela (`formato.ts`).
 */
function RelogioDoTopo() {
  const [agora, definirAgora] = useState(() => dataEHora(instanteAgora()));
  useEffect(() => {
    const id = setInterval(
      () => definirAgora(dataEHora(instanteAgora())),
      1000,
    );
    return () => clearInterval(id);
  }, []);
  return (
    <span className="mono hidden text-[11.5px] text-tinta-3 xl:inline">
      {agora}
    </span>
  );
}

const instanteAgora = () => agoraDoInquilino().toISOString();
